'use client';

import { incrementTelemetry } from 'core-events/actions';
import React, { useState, useEffect, useCallback } from 'react';
import StockblocksClient from 'stockblocks-client';

import { ContentClassEnum, ContentEncoding, DownloadFormats } from '@videoblocks/content-service-client';
import { Tooltip } from '@videoblocks/design-system';
import { ClickLabel } from '@videoblocks/events-ts/lib/storyblocks/content/ClickLabelEnum';
import { DownloadBottom as DownloadIcon } from '@videoblocks/react-icons';
import { AudioStockItem } from '@videoblocks/search-gateway-client';

import Popover from '../../../__temp__design_system/atoms/Popover/Popover';
import sendEvent from '../../events/StockItemClickEvent';
import { DownloadGroup } from '../StockItemTypes';
import { fetchDownloadFormatsById } from './fetchDownloadFormatsByIdAction';

type WindowWithDataLayer = Window & {
    dataLayer: Record<string, unknown>[];
};

declare const window: WindowWithDataLayer;

export interface DownloadButtonProps {
    contentID: number; // ID of the content to download
    stockblocksId: number; // StockBlocks ID to use for download
    isLoggedIn: boolean;
    detailsUrl: string; // URL to content details page
    downloadGroup: DownloadGroup;
    previewUrl: string; // Provide preview URL if logged in but can't download
    rootHovered: boolean;
    className?: string;
    iconClassName?: string;
    hasText?: boolean;
    iconOnLeft?: boolean;
    stockItem: AudioStockItem;
    position?: number; // i.e. index in search results
    onError?: (message: string) => void; // Optional error callback
}

export const DownloadButton: React.FC<DownloadButtonProps> = ({
    contentID,
    stockblocksId,
    isLoggedIn,
    detailsUrl,
    downloadGroup,
    previewUrl,
    rootHovered = false,
    className = '',
    iconClassName = '',
    hasText,
    iconOnLeft = true,
    stockItem,
    position,
    onError,
}) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [showPopover, setShowPopover] = useState(false);
    const [downloadState, setDownloadState] = useState<{ canDownload: boolean; downloadFormats: ContentEncoding[] }>({
        canDownload: downloadGroup.canDownload,
        downloadFormats: [],
    });
    const [isDownloading, setIsDownloading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    const fetchDownloadPermission = async () => {
        if (isLoggedIn) {
            const canDownload = await downloadGroup.getCanDownloadState();
            return canDownload;
        }
        return false;
    };

    const fetchDownloadFormats = async () => {
        const formats = await fetchDownloadFormatsById(contentID);
        if (formats && formats.length > 0) {
            return formats;
        }
        return [];
    };

    function flattenEncodings(formats: DownloadFormats[]): ContentEncoding[] {
        return formats.flatMap((format) => format.encodings?.download ?? []);
    }

    useEffect(() => {
        if (!rootHovered || !isLoggedIn || hasFetched) return;

        const canDownload = fetchDownloadPermission();
        const formats = fetchDownloadFormats();

        void Promise.all([canDownload, formats]).then(([canDownload, formats]) => {
            const flattenedFormats = flattenEncodings(formats);
            setDownloadState({ canDownload, downloadFormats: flattenedFormats });

            if (flattenedFormats.length > 0) {
                setHasFetched(true);
            }
        });
    }, [rootHovered, isLoggedIn, hasFetched]);

    useEffect(() => {
        if (isLoggedIn && downloadGroup.canDownload && downloadState.downloadFormats.length === 0) {
            setHasFetched(false);
        }
    }, [isLoggedIn, downloadGroup.canDownload]);

    const handleDownload = useCallback(
        ({ downloadUrl, errorMessage }: { downloadUrl?: string; errorMessage?: string }) => {
            if (errorMessage) {
                if (onError) {
                    onError(errorMessage);
                }
            } else {
                if (downloadUrl) {
                    window.location.href = downloadUrl;
                }
            }
        },
        [onError],
    );

    const downloadGTMEvent = (stockItemId: number = contentID) => {
        if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push({
                event: 'StockBlocks Event',
                eventCategory: 'Stock Item',
                eventAction: 'item_download',
                eventValue: stockItemId,
                eventLabel: window.location.pathname,
                stockItemId,
            });
        }
    };

    const downloadStockblocksEvent = () => {
        void sendEvent(stockItem, position as number, ClickLabel.DETAILS, document.referrer, window.location.href);
    };

    const handleDownloadClick = async (formatName: string) => {
        setIsDownloading(true);
        setShowPopover(false);

        const errorMessage =
            'Our server encountered an unexpected error.  Please try again or contact customer support.';
        try {
            const response = await StockblocksClient.downloadStockItem(
                stockblocksId,
                formatName,
                ContentClassEnum.Audio,
                await downloadGroup.getUserID(),
            );

            if (response?.data?.downloadUrl) {
                downloadGTMEvent(); // Trigger GTM event
                downloadStockblocksEvent(); // Trigger StockBlocks event
                handleDownload({ downloadUrl: response.data.downloadUrl });
            } else {
                handleDownload({ errorMessage: response.message || errorMessage });
            }
        } catch (error) {
            handleDownload({ errorMessage: errorMessage });
        } finally {
            setIsDownloading(false);
        }
    };

    const handlePreviewDownloadClick = async () => {
        setIsDownloading(true);

        const errorMessage =
            'Our server encountered an unexpected error.  Please try again or contact customer support.';
        try {
            const response = await StockblocksClient.downloadStockItemPreview(
                stockblocksId,
                ContentClassEnum.Audio,
                await downloadGroup.getUserID(),
            );

            if (response?.data?.downloadUrl) {
                void incrementTelemetry('preview.downloaded.stockItemCard');
                handleDownload({ downloadUrl: response.data.downloadUrl });
            } else {
                handleDownload({ errorMessage: response.message || errorMessage });
            }
        } catch (error) {
            handleDownload({ errorMessage: errorMessage });
        } finally {
            setIsDownloading(false);
        }
    };

    const buildButtonText = (text: string) => {
        return <span className={iconOnLeft ? 'ml-1' : 'mr-2'}>{text}</span>;
    };

    const buildDownloadIcon = () => {
        const icon = <DownloadIcon className={`fill-current ${iconClassName}`} />;
        const text = 'Preview';
        return hasText ? (
            <div className="flex flex-nowrap items-center">
                {!iconOnLeft && buildButtonText(text)}
                {icon}
                {iconOnLeft && buildButtonText(text)}
            </div>
        ) : (
            icon
        );
    };

    // --- Not logged in ---
    if (!isLoggedIn) {
        return (
            <Tooltip
                open={showTooltip}
                title="Sign up to Download"
                trigger={
                    <a
                        // TODO: Replace with actual signup or content detail URL
                        href={detailsUrl}
                        className={`relative flex items-center justify-center rounded cursor-pointer border-none ${className}`}
                        aria-label="Sign up to Download"
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                        onFocus={() => setShowTooltip(true)}
                        onBlur={() => setShowTooltip(false)}
                    >
                        {buildDownloadIcon()}
                    </a>
                }
            />
        );
    }

    // --- Logged in, no download permission (preview only) ---
    if (!downloadGroup.canDownload) {
        return (
            <Tooltip
                open={showTooltip}
                title="Download Preview"
                trigger={
                    <button
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        onClick={handlePreviewDownloadClick}
                        disabled={isDownloading}
                        className={`relative flex items-center justify-center rounded cursor-pointer border-none ${className}`}
                        aria-label="Download Preview"
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                        onFocus={() => setShowTooltip(true)}
                        onBlur={() => setShowTooltip(false)}
                    >
                        {buildDownloadIcon()}
                    </button>
                }
            />
        );
    }

    // --- Logged in, can download ---
    return (
        <Tooltip
            open={showTooltip}
            title="Download"
            trigger={
                <div
                    className={className}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    onFocus={() => setShowTooltip(true)}
                    onBlur={() => setShowTooltip(false)}
                >
                    <Popover
                        open={showPopover}
                        onOpenChange={setShowPopover}
                        trigger={
                            <button
                                data-testid="download-button"
                                className={`relative flex items-center content-center justify-center cursor-pointer border-none text-black w-full ${className}`}
                                aria-label="Download"
                                type="button"
                                onClick={() => setShowPopover((v) => !v)}
                            >
                                {buildDownloadIcon()}
                            </button>
                        }
                    >
                        <div className="w-28 bg-white rounded-xl shadow-md inline-flex flex-col justify-start items-start overflow-hidden">
                            {downloadState.downloadFormats.length === 0 ? (
                                <div className="px-2 py-2 text-sm text-gray-500">No formats available</div>
                            ) : (
                                downloadState.downloadFormats.map((format, index) => (
                                    <button
                                        data-testid="download-button"
                                        key={index}
                                        className="w-full px-2 py-2 text-gray-800 text-sm font-semibold leading-tight text-left hover:bg-neutral-100 focus:outline-none"
                                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                        onClick={() => handleDownloadClick(format.format)}
                                        disabled={isDownloading}
                                    >
                                        {format.format?.toUpperCase() || ''}
                                    </button>
                                ))
                            )}
                        </div>
                    </Popover>
                </div>
            }
        />
    );
};

export default DownloadButton;
